import "./style.css";
import { visuallyHidden } from "@mui/utils";
import clsx from "clsx";
import { useEnvContext } from "context/EnvContext";
import { APP_PROVIDER_SEARCH, LOGO_MASTER, TRACK_EVENTS } from "core/consts";
import { useInterval } from "core/hooks";
import { isStaging } from "core/model/utils/featureFlags";
import { dp } from "ds_legacy/materials/metrics";
import { Body, Caption } from "ds_legacy/materials/typography";
import { Spinner as HerouiSpinner } from "ds/ui";
import { RENDER_DELAY, RenderDelay } from "dsl/atoms/LoadingDelay";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTracking } from "react-tracking";
import styled from "styled-components";
import { useTranslations } from "translations";
import { RecareLogoOnly, RecareLogoOnlyProps } from "../RecareLogo";

const SPINNER_SIZES = {
  big: { height: dp(120), width: dp(120) },
  default: { height: dp(80), width: dp(80) },
  small: { height: dp(20), width: dp(20) },
};

const SpinnerContainer = styled.div<{ size: SpinnerSize }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
  height: 100%;
  min-heigth: ${(props) => SPINNER_SIZES[props.size]};
`;

const SpinnerPageContainerStyled = styled.div<{ height?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${({ height }) => height || "100px"};
`;

function SpinnerInfo({ id }: { id: string }) {
  if (!isStaging) return null;
  return <Caption style={{ color: "#efefef" }}>{id}</Caption>;
}

type SpinnerSize = "big" | "default" | "small";

type SpinnerPropsType = {
  delay?: number;
  height?: string;
  id: string;
  size?: SpinnerSize;
  variant?: RecareLogoOnlyProps["variant"];
};

function Spinner({ id, size, variant }: SpinnerPropsType) {
  const [longSpinners, setLongSpinners] = useState(0);
  const { app } = useEnvContext();
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  const interval = 10 * 1000;

  const { height, width } = useMemo(() => {
    if (size) return SPINNER_SIZES[size];
    return SPINNER_SIZES["default"];
  }, [size]);

  const inStorybook = (window as any)?.IN_STORYBOOK;

  useInterval(() => {
    trackEvent({
      duration: (longSpinners + 1) * interval,
      name: TRACK_EVENTS.SPINNER,
      spinner_id: id,
      times: longSpinners + 1,
    });

    setLongSpinners((prev) => prev + 1);
  }, interval);

  return (
    <SpinnerContainer className="spinner-component" size={size ?? "default"}>
      {app === APP_PROVIDER_SEARCH ? (
        <HerouiSpinner
          color="primary"
          size="lg"
          testId="providersearch-spinner"
        />
      ) : (
        <div className={clsx({ "heartbeat-animation": !inStorybook })}>
          <RecareLogoOnly
            style={{ height, width }}
            variant={variant ?? LOGO_MASTER}
          />
        </div>
      )}
      <Body style={visuallyHidden} whiteSpace="nowrap">
        {translations.general.status.loading}
      </Body>
      <SpinnerInfo id={id} />
    </SpinnerContainer>
  );
}

export const SpinnerPageContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [containerOffsetTop, setContainerOffsetTop] = useState(0);

  useLayoutEffect(() => {
    const { current } = ref;
    const offsetTop = (current?.offsetParent as HTMLElement)?.offsetTop;
    setContainerOffsetTop(offsetTop || 0);
  }, [ref]);

  return (
    <SpinnerPageContainerStyled
      ref={ref}
      height={`${window.innerHeight - containerOffsetTop}px`}
    >
      {children}
    </SpinnerPageContainerStyled>
  );
};

export function SpinnerPage({
  delay = RENDER_DELAY,
  id,
  size,
  variant,
}: SpinnerPropsType) {
  return (
    <RenderDelay delay={delay}>
      <Spinner id={id} size={size} variant={variant} />
    </RenderDelay>
  );
}

export default SpinnerPage;
